import { ENetworkRequestStatus, IUser, Upload } from 'services/BackendApi';
import { ETaskRecurringFilter, ETasksSortableKey, ITask, ITaskAttachment, ITaskCategory, ITaskComment, ITaskReminder, ITaskTeam, ITaskUser } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { ESortOrder } from 'store/common/types';
import { ERecurringTasksSortableKey } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { IRecurringTaskListFilters } from 'services/TaskManagementApi/queries/useRecurringTasks';
import { INewUploadPayload } from '../bookingManager/subdomains/uploads/model';

export interface INewUpload {
  payload: INewUploadPayload | null;
  request: ENetworkRequestStatus;
  error: Error | null;
}

export interface ITaskAttachments {
  newAttachment: INewUpload;
  attachments: ITaskAttachment[];
  attachmentsLoad: ENetworkRequestStatus;
  attachmentsLoadError?: Error;
  uploadAttachmentModalOpen: boolean;
  uploadAttachmentError?: Error;
}

// The Reviewer filter's "No reviewer" option; the search sends `reviewerId: null` for it.
export const NO_REVIEWER_FILTER_VALUE = 'no_reviewer';

export interface ITaskManagementDomain {
  filters: {
    id: string;
    categories: string[];
    statuses: string[];
    team: string | null;
    assignee: string | null;
    priority: string | null;
    dates: string[];
    bookingRef: string;
    guestName: string;
    reporter: string;
    // a user uuid, NO_REVIEWER_FILTER_VALUE, or empty for no filter
    reviewer: string;
    // null = All, nothing sent to the search
    recurring: ETaskRecurringFilter | null;
  },
  requests: {
    getTasks: ENetworkRequestStatus;
    getTask: ENetworkRequestStatus;
    getCategories: ENetworkRequestStatus;
    getTeams: ENetworkRequestStatus;
    getReporters: ENetworkRequestStatus;
    getFilterInfo: ENetworkRequestStatus;
    getAssignees: ENetworkRequestStatus;
    getReviewers: ENetworkRequestStatus;
    createTask: ENetworkRequestStatus;
    createRecurringTask: ENetworkRequestStatus;
    updateRecurringTask: ENetworkRequestStatus;
    deleteTask: ENetworkRequestStatus;
    editTask: ENetworkRequestStatus;
    createTaskComment: ENetworkRequestStatus;
    listComments: ENetworkRequestStatus;
    deleteComment: ENetworkRequestStatus;
    getMentionList: ENetworkRequestStatus;
    listAttachments: ENetworkRequestStatus;
    deleteAttachment: ENetworkRequestStatus;
    searchBookingRefs: ENetworkRequestStatus;
  },
  data: {
    categories: ITaskCategory[];
    teams: ITaskTeam[];
    assignees: ITaskUser[];
    reporters: ITaskUser[];
    reviewers: ITaskUser[];
    userCache: ITaskUser[];
    results: ITask[];
    tableLoading: boolean;
    comments: ITaskComment[];
    selectedTask: ITask | null;
    // true when the last getTask call was refused with a 403 / ACCESS_DENIED
    selectedTaskAccessDenied: boolean;
    selectedCommentId: string | null;
    focusedComments: (string | null)[];
    reminders: ITaskReminder[];
    attachments: ITaskAttachments;
    mentionList: Pick<IUser, 'uuid' | 'firstName' | 'lastName' | 'email' | 'type'>[],
    selectedAttachmentId: string | null;
    duplicatedReminderIds: string[];
    bookingRefs: string[];
    // INVALID_RECURRENCE message of the last create / update recurring task attempt
    recurrenceError: string | null;
  },
  pagination: {
    currentPage: number;
    itemsPerPage: number; 
    totalItems: number | null; 
  },
  sort: {
    sortBy: ETasksSortableKey;
    sortOrder: ESortOrder;
  },
  // OWA-11996 - the Recurring Tasks list. Its data comes from react-query (useRecurringTasks).
  recurringTaskList: {
    filters: IRecurringTaskListFilters;
    sort: {
      sortBy: ERecurringTasksSortableKey;
      sortOrder: ESortOrder;
    };
    pagination: {
      currentPage: number;
      itemsPerPage: number;
    };
  },
}

export const initialRecurringTaskListState: ITaskManagementDomain['recurringTaskList'] = {
  filters: {
    subject: '',
    categoryIds: [],
    teamId: '',
    frequencies: [],
    status: '',
  },
  sort: {
    sortBy: ERecurringTasksSortableKey.UPDATED_AT,
    sortOrder: ESortOrder.DESC,
  },
  pagination: {
    currentPage: 0,
    itemsPerPage: 10,
  },
};

export const initialState: ITaskManagementDomain = {
  filters: {
    id: '',
    categories: [],
    statuses: [],
    team: null,
    assignee: null,
    priority: null,
    dates: [],
    bookingRef: '',
    guestName: '',
    reporter: '',
    reviewer: '',
    recurring: null,
  },
  requests: {
    getTasks: ENetworkRequestStatus.IDLE,
    getTask: ENetworkRequestStatus.IDLE,
    getCategories: ENetworkRequestStatus.IDLE,
    getTeams: ENetworkRequestStatus.IDLE,
    getReporters: ENetworkRequestStatus.IDLE,
    getAssignees: ENetworkRequestStatus.IDLE,
    getReviewers: ENetworkRequestStatus.IDLE,
    getFilterInfo: ENetworkRequestStatus.IDLE,
    createTask: ENetworkRequestStatus.IDLE,
    createRecurringTask: ENetworkRequestStatus.IDLE,
    updateRecurringTask: ENetworkRequestStatus.IDLE,
    deleteTask: ENetworkRequestStatus.IDLE,
    editTask: ENetworkRequestStatus.IDLE,
    createTaskComment: ENetworkRequestStatus.IDLE,
    listComments: ENetworkRequestStatus.IDLE,
    deleteComment: ENetworkRequestStatus.IDLE,
    getMentionList: ENetworkRequestStatus.IDLE,
    listAttachments: ENetworkRequestStatus.IDLE,
    deleteAttachment: ENetworkRequestStatus.IDLE,
    searchBookingRefs: ENetworkRequestStatus.IDLE,
  },
  data: {
    categories: [],
    assignees: [],
    reporters: [],
    reviewers: [],
    userCache: [],
    teams: [],
    results: [],
    tableLoading: false,
    selectedTask: null,
    selectedTaskAccessDenied: false,
    comments: [],
    focusedComments: [],
    selectedCommentId: null,
    reminders: [],
    attachments: {
      attachments: [],
      attachmentsLoad: ENetworkRequestStatus.IDLE,
      uploadAttachmentModalOpen: false,
      newAttachment: {
        payload: null,
        request: ENetworkRequestStatus.IDLE,
        error: null,
      },
    },
    mentionList: [],
    selectedAttachmentId: null,
    duplicatedReminderIds: [],
    bookingRefs: [],
    recurrenceError: null,
  },
  pagination: {
    currentPage: 0,
    itemsPerPage: 10,
    totalItems: null,
  },
  sort: {
    sortBy: ETasksSortableKey.DEADLINE,
    sortOrder: ESortOrder.ASC,
  },
  recurringTaskList: initialRecurringTaskListState,
};
